/**
 * FootballStatsCard
 *
 * Gamified football (soccer) career statistics card.
 * Reuses volleyball vb-* CSS classes for visual consistency.
 *
 * Props:
 *   stats  — object returned by teamService.getPlayerCareerStats()
 *   user   — { name, username, ... } from AuthContext
 */
import React from 'react';
import '../../../styles/components/sportStats.css';

const fmt = (n, decimals = 0) => (n == null ? '—' : Number(n).toFixed(decimals));
const perGame = (total, matches, dec = 2) =>
  matches > 0 ? fmt(total / matches, dec) : '—';
const perGameNum = (total, matches) => (matches > 0 ? total / matches : null);

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return null; }
};

const LEVELS = [
  { min: 0,  max: 4,  label: 'ROOKIE',   color: '#94a3b8', next: 5  },
  { min: 5,  max: 14, label: 'AMATEUR',  color: '#4ade80', next: 15 },
  { min: 15, max: 29, label: 'PRO',      color: '#38bdf8', next: 30 },
  { min: 30, max: 49, label: 'ELITE',    color: '#a78bfa', next: 50 },
  { min: 50, max: Infinity, label: 'LEGEND', color: '#fbbf24', next: null },
];
const getLevel = (matches) => {
  const lvl = LEVELS.find((l) => matches >= l.min && matches <= l.max) || LEVELS[0];
  const progress = lvl.next
    ? Math.min(100, ((matches - lvl.min) / (lvl.next - lvl.min)) * 100)
    : 100;
  return { ...lvl, progress };
};

// Shot accuracy donut
const ShotDonut = ({ goals = 0, shotsOnTarget = 0, totalShots = 0 }) => {
  const total = Math.max(totalShots, shotsOnTarget, goals, 1);
  const sotPct = shotsOnTarget / total;
  const goalPct = goals / total;
  const r = 44; const cx = 56; const cy = 56;
  const circ = 2 * Math.PI * r;
  const sotLen  = circ * sotPct;
  const goalLen = circ * goalPct;
  const conversionRate = shotsOnTarget > 0 ? ((goals / shotsOnTarget) * 100).toFixed(1) : '0.0';
  const ringColor = parseFloat(conversionRate) >= 40 ? '#f59e0b' : '#38bdf8';

  return (
    <div className="vb-donut-wrapper">
      <div className="vb-donut-ring-wrap">
        <svg viewBox="0 0 112 112" className="vb-donut-svg">
          <defs>
            <filter id="fbRingGlow">
              <feGaussianBlur stdDeviation="3" result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="13"/>
          <circle cx={cx} cy={cy} r={r} fill="none"
            stroke="rgba(56,189,248,0.4)" strokeWidth="13"
            strokeDasharray={`${sotLen} ${circ - sotLen}`}
            strokeDashoffset={circ * 0.25}
            strokeLinecap="round"/>
          <circle cx={cx} cy={cy} r={r} fill="none"
            stroke={ringColor} strokeWidth="13"
            strokeDasharray={`${goalLen} ${circ - goalLen}`}
            strokeDashoffset={circ * 0.25}
            strokeLinecap="round"
            filter="url(#fbRingGlow)"/>
          <text x={cx} y={cy - 5} textAnchor="middle" className="vb-donut-main">{conversionRate}</text>
          <text x={cx} y={cy + 12} textAnchor="middle" className="vb-donut-sub">CONV %</text>
        </svg>
      </div>
      <div className="vb-donut-legend">
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background: ringColor}}/>
          <span className="vb-donut-text">GOALS: <strong className="vb-donut-val">{goals}</strong></span>
        </div>
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background:'rgba(56,189,248,0.6)'}}/>
          <span className="vb-donut-text">SOT: <strong className="vb-donut-val">{shotsOnTarget}</strong></span>
        </div>
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background:'rgba(255,255,255,0.15)'}}/>
          <span className="vb-donut-text">SHOTS: <strong className="vb-donut-val">{totalShots}</strong></span>
        </div>
      </div>
    </div>
  );
};

const StatSpot = ({ icon, label, value }) => (
  <div className="vb-spot">
    <div className="vb-spot__icon">{icon}</div>
    <div className="vb-spot__value">{value}</div>
    <div className="vb-spot__label">{label}</div>
  </div>
);

const TrophyCard = ({ icon, label, value, detail, rank }) => {
  const colors = { gold: '#fbbf24', silver: '#94a3b8', bronze: '#cd7f32' };
  const c = colors[rank] || 'rgba(255,255,255,0.2)';
  return (
    <div className="vb-trophy" style={{'--trophy-color': c}}>
      <div className="vb-trophy__icon">{icon}</div>
      <div className="vb-trophy__body">
        <div className="vb-trophy__label">{label}</div>
        <div className="vb-trophy__value">{value ?? '—'}</div>
        {detail && <div className="vb-trophy__detail">{detail}</div>}
      </div>
      {value != null && <div className="vb-trophy__badge">PB</div>}
    </div>
  );
};

const BarRow = ({ label, value, rawNum, maxRef }) => {
  const pct = rawNum != null && maxRef > 0 ? Math.min(100, (rawNum / maxRef) * 100) : 0;
  return (
    <div className="vb-bar-row">
      <div className="vb-bar-header">
        <span className="vb-bar-label">{label}</span>
        <span className="vb-bar-val">{value}</span>
      </div>
      <div className="vb-bar-track">
        <div className="vb-bar-fill" style={{width: `${pct}%`}}/>
      </div>
    </div>
  );
};

const MatchChip = ({ match }) => {
  const goals  = match.metrics?.goal       || 0;
  const assists= match.metrics?.assist     || 0;
  const sot    = match.metrics?.shotOnTarget || 0;
  const date   = formatDate(match.matchDate);
  const resultIcon = match.result === 'win' ? '🏆' : match.result === 'loss' ? '❌' : match.result === 'draw' ? '➖' : null;
  const displayTitle = match.matchTitle || (match.matchShortCode ? `#${match.matchShortCode}` : null) || `Match ${match.matchId}`;
  return (
    <div className="vb-match-chip">
      <div className="vb-match-chip__header">
        <div className="vb-match-chip__title">{displayTitle}</div>
        {resultIcon && <span className="vb-match-chip__result">{resultIcon}</span>}
      </div>
      <div className="vb-match-chip__date">{date || '—'}</div>
      <div className="vb-match-chip__stats">
        <span className="vb-chip-stat"><span className="vb-chip-ico">⚽</span>{goals}</span>
        <span className="vb-chip-stat"><span className="vb-chip-ico">🎯</span>{assists}</span>
        <span className="vb-chip-stat"><span className="vb-chip-ico">🎯</span>SOT:{sot}</span>
        {match.metrics?.yellowCard > 0 && (
          <span className="vb-chip-stat"><span className="vb-chip-ico">🟨</span>{match.metrics.yellowCard}</span>
        )}
      </div>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const FootballStatsCard = ({ stats = {}, user = {}, sportProfile = {} }) => {
  const {
    careerTotals  = {},
    careerHighs   = {},
    matchesPlayed  = 0,
    winsCount      = 0,
    lossesCount    = 0,
    drawsCount     = 0,
    scoreContribution = 0,
    recentMatches  = [],
  } = stats;

  const goals          = careerTotals.goal              || scoreContribution || 0;
  const assists        = careerTotals.assist            || 0;
  const shotsOnTarget  = careerTotals.shotOnTarget      || 0;
  const tackles        = careerTotals.tackle            || 0;
  const interceptions  = careerTotals.interception      || 0;
  const saves          = careerTotals.save              || 0;
  const yellowCards    = careerTotals.yellowCard        || 0;
  const redCards       = careerTotals.redCard           || 0;
  const fouls          = careerTotals.foul              || 0;

  // Signature metrics
  const headerGoals    = careerTotals.headerGoal        || 0;
  const outboxGoals    = careerTotals.outsideBoxGoal    || 0;
  const bigChances     = careerTotals.bigChanceCreated  || 0;
  const takeOns        = careerTotals.successfulTakeOn  || 0;
  const penGoals       = careerTotals.penaltyGoal       || 0;

  const totalShots     = careerTotals.shot || Math.max(shotsOnTarget, goals);

  const level = getLevel(matchesPlayed);
  const hasStats = matchesPlayed > 0;

  const gps  = perGameNum(goals,         matchesPlayed);
  const aps  = perGameNum(assists,        matchesPlayed);
  const sps  = perGameNum(shotsOnTarget,  matchesPlayed);
  const tps  = perGameNum(tackles,        matchesPlayed);
  const ips  = perGameNum(interceptions,  matchesPlayed);
  const maxBar = Math.max(gps||0, aps||0, sps||0, tps||0, ips||0, 1);

  const high = (key) => {
    const h = careerHighs[key];
    if (!h) return { value: null, detail: null };
    return {
      value:  h.value,
      detail: h.matchDate ? `vs. ${h.matchTitle || 'match'} · ${formatDate(h.matchDate)}` : null,
    };
  };

  const displayName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim().toUpperCase()
    : (user?.username || 'PLAYER').toUpperCase();

  return (
    <div className="vb-card">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <div className="vb-hero">
        <div className="vb-hero__bg-orbs" aria-hidden="true">
          <span className="vb-orb vb-orb--1"/>
          <span className="vb-orb vb-orb--2"/>
        </div>
        <div className="vb-hero__ball" aria-hidden="true">⚽</div>
        <div className="vb-hero__info">
          <div className="vb-hero__name">{displayName}</div>
          <div className="vb-hero__sport">Football · Career Statistics</div>
          {(sportProfile?.position || sportProfile?.playingStyle) && (
            <div className="vb-hero__profile">
              {sportProfile.position     && <span className="vb-hero__position">📍 {sportProfile.position}</span>}
              {sportProfile.playingStyle && <span className="vb-hero__style">🎮 {sportProfile.playingStyle}</span>}
            </div>
          )}
        </div>
        <div className="vb-hero__meta">
          <div className="vb-level-badge" style={{'--lv-color': level.color}}>
            <span className="vb-level-badge__label">LEVEL</span>
            <span className="vb-level-badge__name">{level.label}</span>
          </div>
          <div className="vb-hero__matches">
            <span className="vb-hero__matches-num">{matchesPlayed}</span>
            <span className="vb-hero__matches-label">{' '}Match{matchesPlayed !== 1 ? 'es' : ''} Played</span>
          </div>
          {(winsCount > 0 || lossesCount > 0) && (
            <div className="vb-hero__record">
              <span className="vb-hero__win">🏆 {winsCount}W</span>
              <span className="vb-hero__sep"> · </span>
              <span className="vb-hero__loss">❌ {lossesCount}L</span>
              {drawsCount > 0 && <><span className="vb-hero__sep"> · </span><span className="vb-hero__draw">➖ {drawsCount}D</span></>}
            </div>
          )}
        </div>
        <div className="vb-xp-bar-wrap">
          <div className="vb-xp-bar-track">
            <div className="vb-xp-bar-fill" style={{width: `${level.progress}%`}}/>
          </div>
          <span className="vb-xp-label">
            {level.next ? `${matchesPlayed} / ${level.next} matches to ${LEVELS.find(l => l.min === level.next)?.label || 'MAX'}` : '🏆 MAX LEVEL'}
          </span>
        </div>
      </div>

      {/* ── SIGNATURE STAT ROW ───────────────────────────────────────────── */}
      {hasStats && (headerGoals + outboxGoals + bigChances + takeOns + penGoals) > 0 && (
        <div className="vb-spots-row" style={{borderBottom:'1px solid rgba(251,191,36,0.2)'}}>
          {headerGoals > 0 && <StatSpot icon="🤜" label="HEADERS"     value={headerGoals}/>}
          {outboxGoals > 0 && <StatSpot icon="💥" label="LONG RANGE"  value={outboxGoals}/>}
          {bigChances > 0  && <StatSpot icon="🔑" label="BIG CHANCES" value={bigChances}/>}
          {takeOns > 0     && <StatSpot icon="💨" label="TAKE-ONS"    value={takeOns}/>}
          {penGoals > 0    && <StatSpot icon="🎯" label="PENALTIES"   value={penGoals}/>}
        </div>
      )}

      {/* ── CORE STAT SPOTLIGHTS ─────────────────────────────────────────── */}
      <div className="vb-spots-row">
        <StatSpot icon="⚽" label="GOALS"   value={hasStats ? goals   : '—'}/>
        <StatSpot icon="🎯" label="ASSISTS" value={hasStats ? assists : '—'}/>
        <StatSpot icon="🎯" label="SOG"     value={hasStats ? shotsOnTarget : '—'}/>
        <StatSpot icon="🛡️" label="TACKLES" value={hasStats ? tackles : '—'}/>
        {saves > 0 && <StatSpot icon="🧤" label="SAVES"   value={saves}/>}
        <StatSpot icon="🟨" label="CARDS"   value={hasStats ? `${yellowCards}Y/${redCards}R` : '—'}/>
      </div>

      <div className="vb-card__body">
        {/* LEFT col */}
        <div className="vb-card__left">
          <h4 className="vb-section-title">🥇 CAREER HIGHS</h4>
          <div className="vb-trophies">
            <TrophyCard icon="⚽" label="GOALS"        rank="gold"   {...high('goal')}/>
            <TrophyCard icon="🎯" label="ASSISTS"       rank="silver" {...high('assist')}/>
            <TrophyCard icon="🎯" label="SHOTS ON TGT"  rank="bronze" {...high('shotOnTarget')}/>
            <TrophyCard icon="🛡️" label="TACKLES"       rank="gold"   {...high('tackle')}/>
            <TrophyCard icon="✂️" label="INTERCEPTIONS" rank="silver" {...high('interception')}/>
            {saves > 0 && <TrophyCard icon="🧤" label="SAVES"        rank="bronze" {...high('save')}/>}
          </div>
        </div>

        {/* RIGHT col */}
        <div className="vb-card__right">
          <h4 className="vb-section-title">📊 SHOT CONVERSION</h4>
          <ShotDonut goals={goals} shotsOnTarget={shotsOnTarget} totalShots={totalShots}/>

          <h4 className="vb-section-title vb-section-title--mt">📈 PER-MATCH AVERAGES</h4>
          <div className="vb-bars">
            <BarRow label="Goals / Match"        value={perGame(goals,        matchesPlayed)} rawNum={gps} maxRef={maxBar}/>
            <BarRow label="Assists / Match"      value={perGame(assists,      matchesPlayed)} rawNum={aps} maxRef={maxBar}/>
            <BarRow label="Shots on Tgt / Match" value={perGame(shotsOnTarget,matchesPlayed)} rawNum={sps} maxRef={maxBar}/>
            <BarRow label="Tackles / Match"      value={perGame(tackles,      matchesPlayed)} rawNum={tps} maxRef={maxBar}/>
            <BarRow label="Interceptions / Match"value={perGame(interceptions,matchesPlayed)} rawNum={ips} maxRef={maxBar}/>
          </div>

          <div className="vb-totals-row">
            <div className="vb-total-chip"><span className="vb-total-val">{fmt(goals)}</span><span className="vb-total-lbl">Goals</span></div>
            <div className="vb-total-chip"><span className="vb-total-val">{fmt(assists)}</span><span className="vb-total-lbl">Assists</span></div>
            <div className="vb-total-chip"><span className="vb-total-val">{fmt(fouls)}</span><span className="vb-total-lbl">Fouls</span></div>
          </div>
        </div>
      </div>

      {recentMatches.length > 0 && (
        <div className="vb-recent">
          <h4 className="vb-section-title vb-section-title--center">🕹️ RECENT MATCHES</h4>
          <div className="vb-match-list">
            {recentMatches.slice(0, 5).map((m) => (
              <MatchChip key={m.matchId} match={m}/>
            ))}
          </div>
        </div>
      )}

      {!hasStats && (
        <div className="vb-empty">
          <div className="vb-empty__ball">⚽</div>
          <div className="vb-empty__heading">YOUR JOURNEY BEGINS HERE</div>
          <p className="vb-empty__sub">No advanced scoring stats recorded yet.</p>
          <p className="vb-empty__cta">
            Play a live match in <strong>Advanced Scoring</strong> mode to start
            unlocking your football career statistics.
          </p>
          <div className="vb-empty__unlock-chips">
            <span className="vb-unlock-chip">⚽ Track Goals</span>
            <span className="vb-unlock-chip">🎯 Track SOG</span>
            <span className="vb-unlock-chip">🛡️ Track Tackles</span>
            <span className="vb-unlock-chip">📊 Shot Conversion</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FootballStatsCard;
